import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CheckCircle2,
  Lock,
  Building2,
  Brain,
  Sparkles,
  AlertCircle,
  Eye,
  EyeOff,
  KeyRound,
  LogIn,
  ScanFace,
  QrCode,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  hasSessionExpiredNotice,
  clearSessionExpiredNotice,
} from "@/services/api";
import { BrandMark } from "@/components/brand/BrandMark";
import { BRAND } from "@/constants/brand";
import { FaceCaptureView } from "@/features/face-liveness/FaceCaptureView";
import { preloadFaceLandmarker } from "@/features/face-liveness/faceLandmarkerLoader";
import { FaceLoginError, fetchFaceLoginAvailable } from "@/services/faceAuth";

interface LoginPageProps {
  onLogin?: (positionCode: string, password: string) => Promise<boolean>;
  onLoginWithFace: (username: string, image: string) => Promise<void>;
  onLoginWithToken: (token: string) => Promise<boolean>;
}

/*
 * FACE ID — ЧЕРЕЗ CompreFace В `sc_atyrau_backend` (28.09.2026). Логин
 * вводится до камеры: бэкенд сверяет кадр 1:1 с лицом этой учётки, а не ищет
 * «кто это» по всей базе. Вкладка работает, только если бэкенд говорит, что
 * CompreFace настроен (`/auth/methods`); иначе на ней заглушка.
 *
 * QR — по-прежнему заглушка: платформы под него у Атырау нет. По умолчанию
 * открыт вход по логину и паролю.
 */
type FacePhase = "capture" | "verifying" | "failed";
type AuthMethod = "login" | "faceId" | "qr";

const AUTH_TABS: {
  id: AuthMethod;
  label: string;
  icon: typeof LogIn;
}[] = [
  { id: "login", label: "Логин", icon: LogIn },
  { id: "faceId", label: "Face ID", icon: ScanFace },
  { id: "qr", label: "QR-код", icon: QrCode },
];

export function LoginPage({
  onLogin,
  onLoginWithFace,
  onLoginWithToken,
}: LoginPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * ПОЛЯ ПУСТЫЕ ВСЕГДА, В ТОМ ЧИСЛЕ В ТЕСТОВОМ РЕЖИМЕ. Здесь подставлялась
   * тестовая пара — на общем стенде и на планшетах людей это значило вход под
   * чужой учёткой одним нажатием. Логин и пароль человек вводит сам.
   */
  const [accessCode, setAccessCode] = useState("");
  /*
   * Показ пароля нужен на планшете сильнее, чем на десктопе: набирают с
   * экранной клавиатуры, вслепую, и опечатку иначе видно только по отказу входа.
   */
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [activeMethod, setActiveMethod] = useState<AuthMethod>("login");
  const [error, setError] = useState(() =>
    hasSessionExpiredNotice() ? "Сессия истекла. Войдите снова." : ""
  );
  useEffect(() => {
    clearSessionExpiredNotice();
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [successByFace, setSuccessByFace] = useState(false);

  const [faceAvailable, setFaceAvailable] = useState(false);
  const [faceOpen, setFaceOpen] = useState(false);
  const [facePhase, setFacePhase] = useState<FacePhase>("capture");
  const [faceError, setFaceError] = useState("");
  const [faceServiceDown, setFaceServiceDown] = useState(false);
  const [faceFrame, setFaceFrame] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchFaceLoginAvailable().then((available) => {
      if (alive) setFaceAvailable(available);
    });
    return () => {
      alive = false;
    };
  }, []);
  // Проверяем isMobile и token - автоматически пропускаем логин
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isMobile = searchParams.get("is_mobile") === "true";
    const token = searchParams.get("token");
    const savedIsMobile = localStorage.getItem("isMobile") === "true";

    if ((isMobile || savedIsMobile) && token) {
      onLoginWithToken(token).then((success) => {
        if (success) {
          const from = location.state?.from;
          const redirectPath = from?.pathname || "/base";
          const searchString = location.search || from?.search || "";
          navigate(redirectPath + searchString, { replace: true });
        }
      });
    }
  }, [location.search, location.state, navigate, onLoginWithToken]);

  const navigateAfterSuccess = () => {
    setTimeout(() => {
      const from = location.state?.from;
      const redirectPath = from?.pathname || "/base";
      const searchParams = from?.search || "";
      navigate(redirectPath + searchParams, { replace: true });
    }, 500);
  };

  const handlePasswordLogin = async () => {
    if (!accessCode.trim() || !password.trim()) {
      setError("Введите код доступа и пароль");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const success = await onLogin?.(accessCode, password);
      if (success) {
        setScanSuccess(true);
        navigateAfterSuccess();
      } else {
        setError("Неверный код доступа или пароль");
      }
    } catch (err: any) {
      setError(err?.message || "Ошибка авторизации");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handlePasswordLogin();
  };

  const switchMethod = (method: AuthMethod) => {
    setActiveMethod(method);
    setError("");
    /*
     * Модель (~4 МБ) и WASM (~11 МБ) начинают грузиться с выбора вкладки, а не
     * с открытия камеры: пока человек вводит логин, загрузка уже идёт.
     */
    if (method === "faceId" && faceAvailable) preloadFaceLandmarker();
  };

  const openFace = () => {
    if (!accessCode.trim()) {
      setError("Введите код доступа");
      return;
    }
    setError("");
    setFacePhase("capture");
    setFaceError("");
    setFaceServiceDown(false);
    setFaceFrame(null);
    setFaceOpen(true);
  };

  const onFaceCaptured = useCallback(
    async (image: string) => {
      setFaceFrame(image);
      setFacePhase("verifying");
      try {
        await onLoginWithFace(accessCode, image);
        setFaceOpen(false);
        setSuccessByFace(true);
        setScanSuccess(true);
        navigateAfterSuccess();
      } catch (err) {
        setFaceServiceDown(
          err instanceof FaceLoginError && err.code === "face_auth_unavailable",
        );
        setFaceError(err instanceof Error ? err.message : "Не удалось войти по лицу");
        setFacePhase("failed");
      }
    },
    // navigateAfterSuccess пересоздаётся каждый рендер, но читает только
    // location/navigate — те же, что и в момент открытия камеры.
    [accessCode, onLoginWithFace],
  );

  const faceToPassword = () => {
    setFaceOpen(false);
    setActiveMethod("login");
  };

  const accessCodeField = (
    <div className="space-y-2">
      <label
        htmlFor="accessCode"
        className="block text-xs font-medium text-gray-600"
      >
        Код доступа
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <KeyRound className="h-4 w-4 text-gray-400" />
        </div>
        <input
          id="accessCode"
          type="text"
          value={accessCode}
          onChange={(e) => setAccessCode(e.target.value)}
          placeholder="Введите код доступа"
          className="block w-full pl-9 pr-3 py-2.5 text-sm border border-cn-border rounded-cn-lg text-cn-text placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cn-primary/30 focus:border-cn-primary transition-all"
          disabled={isLoading}
        />
      </div>
    </div>
  );

  const errorBlock = error ? (
    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2.5">
      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
      <p className="text-xs leading-snug text-red-700">{error}</p>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-cn-layout flex items-center justify-center p-4 ipad:p-6">
      <div className="w-full max-w-[58rem] flex flex-col ipad:flex-row-reverse items-stretch rounded-cn-xl shadow-2xl overflow-hidden border border-cn-border/60 bg-white min-h-[min(100%,620px)]">
        {/* Бренд и преимущества */}
        <div className="login-brand-panel flex flex-col flex-1 min-w-0 min-h-full p-8 ipad:p-10">
            <div className="flex-shrink-0 mb-6 ipad:mb-8">
              <BrandMark variant="light" showPanelTitle size="xl" />
            </div>

            <div className="flex-1 flex flex-col justify-center min-h-0 py-2">
            <div className="space-y-4 ipad:space-y-5 -translate-y-1.5 ipad:-translate-y-2">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                  <Building2 className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-0.5 leading-snug">
                    Обстановка в области
                  </h3>
                  <p className="text-xs text-white/75 leading-snug">
                    Единая картина по районам Атырауской области и
                    государственным базам
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                  <Brain className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-0.5 leading-snug">
                    Интеллектуальное ядро
                  </h3>
                  <p className="text-xs text-white/75 leading-snug">
                    Аналитика, мониторинг показателей и поддержка решений
                    руководства в режиме реального времени.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-0.5 leading-snug">
                    ИИ-ассистент
                  </h3>
                  <p className="text-xs text-white/75 leading-snug">
                    Подготовка справок, сравнение регионов и рекомендации для
                    принятия управленческих решений
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                  <Lock className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-0.5 leading-snug">
                    Защита данных
                  </h3>
                  <p className="text-xs text-white/75 leading-snug">
                    Соответствие требованиям информационной безопасности
                  </p>
                </div>
              </div>
            </div>
            </div>

            <div className="pt-6 border-t border-white/15 flex-shrink-0">
              <p className="text-xs text-white/60">{BRAND.copyrightShort}</p>
            </div>
        </div>

        {/* Форма входа */}
        <div className="flex flex-1 min-w-0 min-h-full flex-col justify-center px-8 pt-8 pb-0 ipad:px-10 ipad:pt-10 bg-white border-t ipad:border-t-0 ipad:border-r border-cn-border/80">
            <div className="w-full min-h-0 overflow-y-auto py-2">
            <div className="mb-5">
              <h2 className="text-lg ipad:text-xl font-bold text-cn-text text-center">
                Вход в систему
              </h2>
            </div>

            <div
              className="flex rounded-cn-lg border border-cn-border p-1 bg-gray-50 mb-5"
              role="tablist"
              aria-label="Способ входа"
            >
              {AUTH_TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeMethod === id}
                  onClick={() => switchMethod(id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs ipad:text-sm font-medium transition-colors min-h-[40px] ${
                    activeMethod === id
                      ? "bg-cn-primary text-white shadow-sm"
                      : "text-cn-text-secondary hover:text-cn-text hover:bg-white/90"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>

            {activeMethod === "login" && (
              <form onSubmit={handleLoginSubmit} className="space-y-4">
                {accessCodeField}
                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="block text-xs font-medium text-gray-600"
                  >
                    Пароль
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={passwordVisible ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Введите пароль"
                      className="block w-full pl-9 pr-11 py-2.5 text-sm border border-cn-border rounded-cn-lg text-cn-text placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cn-primary/30 focus:border-cn-primary transition-all"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((v) => !v)}
                      disabled={isLoading}
                      /*
                       * `tabIndex={-1}`: с клавиатуры из поля пароля Tab должен
                       * вести на «Войти», а не на глазок.
                       */
                      tabIndex={-1}
                      aria-label={
                        passwordVisible ? "Скрыть пароль" : "Показать пароль"
                      }
                      aria-pressed={passwordVisible}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-cn-text transition-colors disabled:opacity-50"
                    >
                      {passwordVisible ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={
                    isLoading || !accessCode.trim() || !password.trim()
                  }
                  className="w-full h-11 text-sm font-semibold bg-cn-primary hover:bg-cn-primary-dark text-white rounded-cn-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Вход в систему...
                    </div>
                  ) : scanSuccess ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Вход выполнен
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <LogIn className="w-4 h-4" />
                      Войти
                    </div>
                  )}
                </Button>
                {errorBlock}
              </form>
            )}

            {activeMethod === "faceId" && faceAvailable && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  openFace();
                }}
                className="space-y-4"
              >
                {accessCodeField}
                <div className="bg-[#f0f5fa] border border-cn-border rounded-cn-lg p-4">
                  <div className="flex items-start gap-2.5">
                    <ScanFace className="w-5 h-5 text-cn-primary flex-shrink-0 mt-0.5" />
                    <p className="text-xs leading-snug text-cn-text-secondary">
                      Откроется камера. Держите лицо в овале, смотрите прямо —
                      снимок сделается сам.
                    </p>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={isLoading || !accessCode.trim()}
                  className="w-full h-11 text-sm font-semibold bg-cn-primary hover:bg-cn-primary-dark text-white rounded-cn-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  <div className="flex items-center gap-2">
                    <ScanFace className="w-4 h-4" />
                    Войти по лицу
                  </div>
                </Button>
                {errorBlock}
              </form>
            )}

            {activeMethod === "faceId" && !faceAvailable && (
              <div className="space-y-4">
                <div className="bg-[#f0f5fa] border border-cn-border rounded-cn-lg p-4">
                  <div className="flex items-start gap-2.5">
                    <ScanFace className="w-5 h-5 text-cn-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-cn-text mb-0.5">
                        Вход по Face ID
                      </h3>
                      <p className="text-xs leading-snug text-cn-text-secondary">
                        На этом сервере вход по лицу не настроен. Войдите по коду доступа и паролю на вкладке «Логин».
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  disabled
                  className="w-full h-11 text-sm font-semibold bg-cn-primary/60 text-white rounded-cn-lg cursor-not-allowed"
                >
                  Недоступно
                </Button>
              </div>
            )}

            {activeMethod === "qr" && (
              <div className="space-y-4">
                <div className="bg-[#f0f5fa] border border-cn-border rounded-cn-lg p-4">
                  <div className="flex items-start gap-2.5">
                    <QrCode className="w-5 h-5 text-cn-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-cn-text mb-0.5">
                        Вход по QR-коду
                      </h3>
                      <p className="text-xs leading-snug text-cn-text-secondary">
                        Этот способ входа появится позже. Сейчас войдите по коду доступа и паролю на вкладке «Логин».
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  disabled
                  className="w-full h-11 text-sm font-semibold bg-cn-primary/60 text-white rounded-cn-lg cursor-not-allowed"
                >
                  Скоро будет доступно
                </Button>
              </div>
            )}

              {/* Дополнительная информация */}
              <div className="mt-6 pt-6 pb-6 border-t border-gray-200">
                <div className="text-xs text-gray-500 space-y-2">
                  <p className="flex items-center gap-2">
                    <Lock className="w-3 h-3" />
                    Подключение защищено протоколом SSL/TLS
                  </p>
                  <p>
                    При возникновении проблем с доступом обратитесь в службу
                    технической поддержки:{" "}
                    <a
                      href={`mailto:${BRAND.supportEmail}`}
                      className="text-cn-primary hover:underline"
                    >
                      {BRAND.supportEmail}
                    </a>
                    {", "}
                    <a
                      href={`tel:${BRAND.supportPhone.replace(/\s/g, "")}`}
                      className="text-cn-primary hover:underline"
                    >
                      {BRAND.supportPhone}
                    </a>
                  </p>
                </div>
              </div>
            </div>
        </div>
      </div>

      {/* Камера входа по лицу */}
      {faceOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="face-login-title"
        >
          <div className="relative bg-white rounded-3xl shadow-2xl p-6 ipad:p-8 max-w-md w-full">
            <button
              type="button"
              onClick={() => setFaceOpen(false)}
              disabled={facePhase === "verifying"}
              aria-label="Закрыть"
              className="absolute right-4 top-4 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-cn-text disabled:opacity-40"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 id="face-login-title" className="text-base font-semibold text-cn-text pr-10">
              Вход по лицу
            </h3>
            <p className="text-xs text-cn-text-secondary mb-5 truncate">{accessCode.trim()}</p>

            {facePhase === "capture" ? (
              <FaceCaptureView
                onCaptured={onFaceCaptured}
                requireBlink={import.meta.env.VITE_FACE_REQUIRE_BLINK === "true"}
              />
            ) : (
              <div className="flex flex-col items-center gap-4 text-center">
                {faceFrame && (
                  <img
                    src={faceFrame}
                    alt="Снимок, отправленный на проверку"
                    className={`w-[min(60vw,220px)] aspect-[4/5] rounded-[50%] object-cover -scale-x-100 ${
                      facePhase === "verifying" ? "animate-pulse" : "opacity-60"
                    }`}
                  />
                )}
                {facePhase === "verifying" ? (
                  <p className="text-sm font-medium text-cn-text" role="status">
                    Проверяем лицо…
                  </p>
                ) : (
                  <>
                    <div role="alert">
                      <p className="text-sm font-semibold text-cn-text">Не удалось войти</p>
                      <p className="text-xs text-cn-text-secondary mt-1">{faceError}</p>
                    </div>
                    <div className="flex w-full flex-col gap-2">
                      {!faceServiceDown && (
                        <Button
                          type="button"
                          onClick={() => setFacePhase("capture")}
                          className="w-full h-11 text-sm font-semibold bg-cn-primary hover:bg-cn-primary-dark text-white rounded-cn-lg"
                        >
                          Попробовать ещё раз
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={faceToPassword}
                        className="w-full h-11 rounded-cn-lg border border-cn-border text-sm font-medium text-cn-text hover:bg-gray-50"
                      >
                        Войти по паролю
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно успешной верификации */}
      {scanSuccess && !isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="flex flex-col items-center gap-6">
              <div className="w-32 h-32 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-16 h-16 text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-gray-900 mb-1.5">
                  {successByFace ? "Лицо успешно верифицировано" : "Вход выполнен"}
                </p>
                <p className="text-xs text-gray-600">
                  Выполняется вход в систему...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
